import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  /** data-testid forwarded to the wrapper div (trigger gets `${testId}-trigger`). */
  testId?: string;
}

const DropdownWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const DropdownTrigger = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.fast};
  
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.borderDark};
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryLight};
  }
  
  ${({ $disabled, theme }) => $disabled && `
    background: ${theme.colors.backgroundTertiary};
    cursor: not-allowed;
    opacity: 0.7;
  `}
`;

const TriggerContent = styled.span<{ $isPlaceholder?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ $isPlaceholder, theme }) => $isPlaceholder ? theme.colors.textTertiary : theme.colors.text};
`;

const ChevronIcon = styled.span<{ $isOpen: boolean }>`
  transition: transform ${({ theme }) => theme.transitions.fast};
  transform: rotate(${({ $isOpen }) => $isOpen ? '180deg' : '0deg'});
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  max-height: 250px;
  overflow-y: auto;
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transform: translateY(${({ $isOpen }) => $isOpen ? '0' : '-8px'});
  transition: all ${({ theme }) => theme.transitions.fast};
`;

const DropdownItem = styled.button<{ $selected?: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ $selected, theme }) => $selected ? theme.colors.primaryLight : 'transparent'};
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  disabled = false,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <DropdownWrapper ref={wrapperRef} data-testid={testId}>
      {label && <Label>{label}</Label>}
      <DropdownTrigger
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={selectedOption?.label ?? placeholder}
        data-testid={testId ? `${testId}-trigger` : undefined}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        $disabled={disabled}
      >
        <TriggerContent $isPlaceholder={!selectedOption}>
          {selectedOption?.icon}
          {selectedOption?.label || placeholder}
        </TriggerContent>
        <ChevronIcon $isOpen={isOpen}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </ChevronIcon>
      </DropdownTrigger>
      <DropdownMenu role="listbox" $isOpen={isOpen}>
        {options.map(option => (
          <DropdownItem
            key={option.value}
            type="button"
            role="option"
            aria-selected={option.value === value}
            $selected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            {option.icon}
            {option.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownWrapper>
  );
};

